import type React from "react";

/**
 * Renderizador de markdown leve, suficiente pros textos que os agentes/IA
 * geram aqui (dossiês, relatórios, dossiê de concorrente, relatório ESCALA):
 * parágrafos, listas (`- `/`* `/`1.`), sub-headers, **negrito** e `code`.
 * Não é um parser completo de markdown — propositalmente minimalista.
 */

/** Quebra um markdown em seções por headers `#`..`####`. O texto antes do
 *  primeiro header vira uma seção sem título. */
export function splitMdSections(md: string): { title: string; body: string }[] {
  const lines = (md ?? "").split("\n");
  const out: { title: string; body: string }[] = [];
  let cur: { title: string; body: string } = { title: "", body: "" };
  for (const line of lines) {
    const m = line.match(/^#{1,4}\s+(.*)$/);
    if (m) {
      if (cur.title || cur.body.trim()) out.push(cur);
      cur = { title: m[1]!.trim(), body: "" };
    } else {
      cur.body += line + "\n";
    }
  }
  if (cur.title || cur.body.trim()) out.push(cur);
  return out;
}

/** Spans inline: **negrito**, *itálico*, `code`. Renderiza como elementos React
 *  reais — NÃO usa dangerouslySetInnerHTML, então é seguro contra XSS mesmo
 *  com texto vindo da IA / fontes externas (Apify/Perplexity/scrape). */
export function MdInline({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  // Ordem da alternância importa: **bold** vence sobre *italic*.
  const re = /\*\*([^*]+)\*\*|`([^`]+)`|\*([^*\n]+)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] != null) parts.push(<strong key={i++}>{m[1]}</strong>);
    else if (m[2] != null)
      parts.push(
        <code
          key={i++}
          style={{
            background: "var(--wf-ink-6, rgba(0,0,0,0.05))",
            padding: "1px 4px",
            borderRadius: 4,
            fontSize: "0.9em",
          }}
        >
          {m[2]}
        </code>,
      );
    else if (m[3] != null) parts.push(<em key={i++}>{m[3]}</em>);
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

/** Bloco de markdown leve: parágrafos, listas, sub-headers, negrito, code. */
export function MarkdownLite({
  body,
  style,
}: {
  body: string;
  style?: React.CSSProperties;
}) {
  const blocks: React.ReactNode[] = [];
  const lines = (body ?? "").split("\n");
  let listBuf: string[] = [];
  let paraBuf: string[] = [];
  let k = 0;
  const flushList = () => {
    if (listBuf.length === 0) return;
    blocks.push(
      <ul key={`l${k++}`} style={{ margin: "6px 0", paddingLeft: 20, lineHeight: 1.6 }}>
        {listBuf.map((it, j) => (
          <li key={j} style={{ marginBottom: 3 }}>
            <MdInline text={it} />
          </li>
        ))}
      </ul>,
    );
    listBuf = [];
  };
  const flushPara = () => {
    if (paraBuf.length === 0) return;
    const txt = paraBuf.join(" ").trim();
    if (txt)
      blocks.push(
        <p key={`p${k++}`} style={{ margin: "6px 0", lineHeight: 1.6 }}>
          <MdInline text={txt} />
        </p>,
      );
    paraBuf = [];
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const li = line.match(/^\s*(?:[-*]|\d+\.)\s+(.*)$/);
    if (li) {
      flushPara();
      listBuf.push(li[1]!);
      continue;
    }
    if (line.trim() === "") {
      flushList();
      flushPara();
      continue;
    }
    const sub = line.match(/^(#{1,6})\s+(.*)$/);
    if (sub) {
      flushList();
      flushPara();
      // Tamanho cresce conforme o nível do título — leitura muito mais fácil
      // que o tamanho herdado (13px). H1 e H2 são os mais visíveis no doc.
      const level = sub[1]!.length;
      const sizes: Record<number, { fontSize: number; mt: number; mb: number }> = {
        1: { fontSize: 28, mt: 22, mb: 6 },
        2: { fontSize: 22, mt: 18, mb: 4 },
        3: { fontSize: 17, mt: 14, mb: 3 },
        4: { fontSize: 15, mt: 12, mb: 2 },
        5: { fontSize: 14, mt: 10, mb: 2 },
        6: { fontSize: 13, mt: 10, mb: 2 },
      };
      const s = sizes[level] ?? sizes[3]!;
      blocks.push(
        <div
          key={`h${k++}`}
          style={{
            fontWeight: 700,
            fontSize: s.fontSize,
            lineHeight: 1.2,
            marginTop: s.mt,
            marginBottom: s.mb,
            color: "var(--wf-ink)",
            fontFamily: level <= 2 ? "var(--wf-font-display)" : undefined,
          }}
        >
          {sub[2]}
        </div>,
      );
      continue;
    }
    flushList();
    paraBuf.push(line);
  }
  flushList();
  flushPara();
  return <div style={{ fontSize: 13, color: "var(--wf-ink-2)", ...style }}>{blocks}</div>;
}
